import { takeEvery, put } from "redux-saga/effects";
import {
  AGGREGATOR_ACTION_TYPES,
  AGGREGATOR_BULK_ACTION_TYPES,
  AGGREGATOR_TYPE,
} from "../../data/outlet-status-constants";
import api from "../../services/api";
import { ENDPOINT } from "../../services/apiUrls";
import {
  saveAllUsersData,
  getAllUsers,
  getOutletsData,
  saveOutletsData,
  saveActivityLogs,
  getActivityLogs,
  saveActivityErrorDetails,
  getActivityErrorDetails,
  toggleOutletStatus,
  syncSingleOutletMenu,
  outletStatusBulkUpdate,
  outletBulkMenuSync,
  getHelpCentreLink,
  updateOutletLogisticStatus,
  getOutletLogisticStatus,
  saveOutletOnlineStatus,
  getOutletOnlineStatus,
  getSelfDeliveryChargeData,
  updateSelfDeliveryCharge,
  getDeliveryTimings,
  updateDeliveryTimings,
  getSelfDeliveryTimings,
  updateSelfDeliveryTimings,
  getOutletConfiguration,
  getAllWebhookEntities,
  saveAllWebhookEntities,
  saveOutletConfiguration,
  savePaymentProviders,
  getPaymentProviders,
  getCurrentSyncStatus,
  getActivityDownload,
} from "../slices";

export interface ResponseGenerator {
  config?: any;
  data?: any;
  headers?: any;
  request?: any;
  status?: number;
  statusText?: string;
  code?: string;
}

function* fetchAllUsers() {
  try {
    const response: ResponseGenerator = yield api.request({
      method: `GET`,
      url: ENDPOINT.OUTLET_STATUS.fetchAllUsersApi,
    });
    if (!response.code) yield put(saveAllUsersData(response));
  } catch (error) {
    console.log(error);
  }
}

function* fetchOutletsData(actions: any) {
  try {
    const response: ResponseGenerator = yield api.request({
      method: `POST`,
      url: ENDPOINT.OUTLET_STATUS.fetchAllOutletsApi,
      data: actions.payload.dataToPost,
    });
    if (!response.code)
      yield put(
        saveOutletsData({ response, saveStatus: actions?.payload?.saveStatus })
      );
    if (actions?.payload?.callback) actions?.payload?.callback(true);
  } catch (error) {
    console.log(error);
    if (actions?.payload?.callback) actions?.payload?.callback(false);
  }
}

function* fetchOutletOnlineStatus(actions: any) {
  const actionCode =
    actions.payload?.dataToPost?.type === AGGREGATOR_TYPE.zomato.id
      ? AGGREGATOR_ACTION_TYPES.get_outlet_delivery_status
      : AGGREGATOR_ACTION_TYPES.get_outlet_status;
  try {
    const response: ResponseGenerator = yield api.request({
      method: `POST`,
      url: ENDPOINT.OUTLET_STATUS.outletActionApi,
      data: {
        ...actions.payload.dataToPost,
        action: actionCode,
      },
    });
    if (!response.code) {
      let outletId = actions.payload?.dataToPost?.outletId,
        type = actions.payload?.dataToPost?.type;

      yield put(saveOutletOnlineStatus({ response, outletId, type }));
      actions.payload.callback && actions.payload.callback(response);
    } else {
      actions.payload.callback && actions.payload.callback(response);
    }
  } catch (error) {
    console.log(error);
    actions.payload.callback && actions.payload.callback({});
  }
}

function* fetchActivityLogs(actions: any) {
  try {
    const response: ResponseGenerator = yield api.request({
      method: `POST`,
      url: ENDPOINT.OUTLET_STATUS.fetchActivityLogsApi,
      data: actions.payload.dataToPost,
    });
    if (!response.code) yield put(saveActivityLogs(response));
  } catch (error) {
    console.log(error);
  }
}

function* fetchActivityErrorDetails(actions: any) {
  try {
    const response: ResponseGenerator = yield api.request({
      method: `GET`,
      url: ENDPOINT.OUTLET_STATUS.fetchActivityErrorDetailsApi(
        actions.payload.mid
      ),
    });
    if (!response.code) yield put(saveActivityErrorDetails(response));
  } catch (error) {
    console.log(error);
  }
}

function* outletStatusToggle(actions: any) {
  try {
    const response: ResponseGenerator = yield api.request({
      method: `POST`,
      url: ENDPOINT.OUTLET_STATUS.outletActionApi,
      data: {
        ...actions.payload.dataToPost,
        action: AGGREGATOR_ACTION_TYPES.toggle_outlet_status,
      },
    });
    if (!response.code) {
      actions.payload.callback && actions.payload.callback(true);
    } else {
      actions.payload.callback && actions.payload.callback(false);
    }
  } catch (error) {
    console.log(error);
    actions.payload.callback && actions.payload.callback(false);
  }
}

function* singleOutletMenuSync(actions: any) {
  try {
    const response: ResponseGenerator = yield api.request({
      method: `POST`,
      url: ENDPOINT.OUTLET_STATUS.singleOutletMenuSyncApi,
      data: actions.payload.dataToPost,
    });
    if (!response.code) {
      actions.payload.callback && actions.payload.callback(true);
    } else {
      actions.payload.callback && actions.payload.callback(false);
    }
  } catch (error) {
    console.log(error);
    actions.payload.callback && actions.payload.callback(false);
  }
}

function* bulkOutletStatusUpdate(actions: any) {
  try {
    const response: ResponseGenerator = yield api.request({
      method: `POST`,
      url: ENDPOINT.OUTLET_STATUS.outletBulkActionApi,
      data: {
        ...actions.payload.dataToPost,
        action: AGGREGATOR_BULK_ACTION_TYPES.bulk_outlet_status_update,
      },
    });
    if (!response.code) {
      actions.payload.callback && actions.payload.callback(true);
    } else {
      actions.payload.callback && actions.payload.callback(false);
    }
  } catch (error) {
    console.log(error);
    actions.payload.callback && actions.payload.callback(false);
  }
}

function* bulkOutletMenuSync(actions: any) {
  try {
    const response: ResponseGenerator = yield api.request({
      method: `POST`,
      url: ENDPOINT.OUTLET_STATUS.outletBulkActionApi,
      data: {
        ...actions.payload.dataToPost,
        action: AGGREGATOR_BULK_ACTION_TYPES.bulk_menu_sync,
      },
    });
    if (!response.code) {
      actions.payload.callback && actions.payload.callback(true);
    } else {
      actions.payload.callback && actions.payload.callback(false);
    }
  } catch (error) {
    console.log(error);
    actions.payload.callback && actions.payload.callback(false);
  }
}

function* fetchHelpCentreLink(actions: any) {
  try {
    const response: ResponseGenerator = yield api.request({
      method: `POST`,
      url: ENDPOINT.OUTLET_STATUS.outletActionApi,
      data: {
        ...actions.payload.dataToPost,
        action: AGGREGATOR_ACTION_TYPES.get_help_centre_link,
      },
    });
    if (!response.code) {
      actions.payload.callback && actions.payload.callback(true, response);
    } else {
      actions.payload.callback && actions.payload.callback(false);
    }
  } catch (error) {
    console.log(error);
    actions.payload.callback && actions.payload.callback(false);
  }
}

function* fetchOutletLogisticStatus(actions: any) {
  try {
    const response: ResponseGenerator = yield api.request({
      method: `POST`,
      url: ENDPOINT.OUTLET_STATUS.outletActionApi,
      data: {
        ...actions.payload.dataToPost,
        action: AGGREGATOR_ACTION_TYPES.get_outlet_logistic_status,
      },
    });
    if (!response.code) {
      actions.payload.callback && actions.payload.callback(true, response);
    } else {
      actions.payload.callback && actions.payload.callback(false, response);
    }
  } catch (error) {
    console.log(error);
    actions.payload.callback && actions.payload.callback(false);
  }
}

function* outletLogisticStatusUpdate(actions: any) {
  try {
    const response: ResponseGenerator = yield api.request({
      method: `POST`,
      url: ENDPOINT.OUTLET_STATUS.outletActionApi,
      data: {
        ...actions.payload.dataToPost,
        action: AGGREGATOR_ACTION_TYPES.update_outlet_logistic_status,
      },
    });
    if (!response.code) {
      actions.payload.callback && actions.payload.callback(true, response);
    } else {
      actions.payload.callback && actions.payload.callback(false);
    }
  } catch (error) {
    console.log(error);
    actions.payload.callback && actions.payload.callback(false);
  }
}

function* fetchSelfDeliveryChargeData(actions: any) {
  try {
    const response: ResponseGenerator = yield api.request({
      method: `POST`,
      url: ENDPOINT.OUTLET_STATUS.outletActionApi,
      data: {
        ...actions.payload.dataToPost,
        action: AGGREGATOR_ACTION_TYPES.get_self_delivery_charge,
      },
    });
    if (!response.code) {
      actions.payload.callback && actions.payload.callback(true, response);
    } else {
      actions.payload.callback && actions.payload.callback(false, response);
    }
  } catch (error) {
    console.log(error);
    actions.payload.callback && actions.payload.callback(false);
  }
}

function* selfDeliveryChargeUpdate(actions: any) {
  try {
    const response: ResponseGenerator = yield api.request({
      method: `POST`,
      url: ENDPOINT.OUTLET_STATUS.outletActionApi,
      data: {
        ...actions.payload.dataToPost,
        action: AGGREGATOR_ACTION_TYPES.update_self_delivery_charge,
      },
    });
    if (!response.code) {
      actions.payload.callback && actions.payload.callback(true, response);
    } else {
      actions.payload.callback && actions.payload.callback(false, response);
    }
  } catch (error) {
    console.log(error);
    actions.payload.callback && actions.payload.callback(false);
  }
}

function* fetchDeliveryTimings(actions: any) {
  try {
    const response: ResponseGenerator = yield api.request({
      method: `POST`,
      url: ENDPOINT.OUTLET_STATUS.outletActionApi,
      data: {
        ...actions.payload.dataToPost,
        action: AGGREGATOR_ACTION_TYPES.get_delivery_timings,
      },
    });
    if (!response.code) {
      actions.payload.callback && actions.payload.callback(true, response);
    } else {
      actions.payload.callback && actions.payload.callback(false, response);
    }
  } catch (error) {
    console.log(error);
    actions.payload.callback && actions.payload.callback(false);
  }
}

function* deliveryTimingsUpdate(actions: any) {
  try {
    const response: ResponseGenerator = yield api.request({
      method: `POST`,
      url: ENDPOINT.OUTLET_STATUS.outletActionApi,
      data: {
        ...actions.payload.dataToPost,
        action: AGGREGATOR_ACTION_TYPES.update_delivery_timings,
      },
    });
    if (!response.code) {
      actions.payload.callback && actions.payload.callback(true, response);
    } else {
      actions.payload.callback && actions.payload.callback(false, response);
    }
  } catch (error) {
    console.log(error);
    actions.payload.callback && actions.payload.callback(false);
  }
}

function* fetchSelfDeliveryTimings(actions: any) {
  try {
    const response: ResponseGenerator = yield api.request({
      method: `POST`,
      url: ENDPOINT.OUTLET_STATUS.outletActionApi,
      data: {
        ...actions.payload.dataToPost,
        action: AGGREGATOR_ACTION_TYPES.get_self_delivery_timings,
      },
    });
    if (!response.code) {
      actions.payload.callback && actions.payload.callback(true, response);
    } else {
      actions.payload.callback && actions.payload.callback(false, response);
    }
  } catch (error) {
    console.log(error);
    actions.payload.callback && actions.payload.callback(false);
  }
}

function* selfDeliveryTimingsUpdate(actions: any) {
  try {
    const response: ResponseGenerator = yield api.request({
      method: `POST`,
      url: ENDPOINT.OUTLET_STATUS.outletActionApi,
      data: {
        ...actions.payload.dataToPost,
        action: AGGREGATOR_ACTION_TYPES.update_self_delivery_timings,
      },
    });
    if (!response.code) {
      actions.payload.callback && actions.payload.callback(true, response);
    } else {
      actions.payload.callback && actions.payload.callback(false, response);
    }
  } catch (error) {
    console.log(error);
    actions.payload.callback && actions.payload.callback(false);
  }
}

function* fetchOutletConfiguration(actions: any) {
  try {
    const response: ResponseGenerator = yield api.request({
      method: `GET`,
      url: ENDPOINT.OUTLET_STATUS.getOutletConfiguration(
        actions?.payload?.outletId
      ),
    });
    if (!response.code) {
      actions.payload.callback && actions.payload.callback(true, response);
    } else {
      actions.payload.callback && actions.payload.callback(false, response);
    }
  } catch (error) {
    console.log(error);
    actions.payload.callback && actions.payload.callback(false);
  }
}

function* outletConfigurationSave(actions: any) {
  try {
    const response: ResponseGenerator = yield api.request({
      method: `POST`,
      url: ENDPOINT.OUTLET_STATUS.saveOutletConfiguration,
      data: actions.payload.dataToPost,
    });
    if (!response.code) {
      actions.payload.callback && actions.payload.callback(true);
    } else {
      actions.payload.callback && actions.payload.callback(false);
    }
  } catch (error) {
    console.log(error);
    actions.payload.callback && actions.payload.callback(false);
  }
}

function* fetchAllWebhookEntities(actions: any) {
  try {
    const response: ResponseGenerator = yield api.request({
      method: `GET`,
      url: ENDPOINT.OUTLET_STATUS.getAllWebhookEntities,
    });
    if (!response.code) {
      actions.payload.callback && actions.payload.callback(true);
      yield put(saveAllWebhookEntities({ response }));
    } else {
      actions.payload.callback && actions.payload.callback(false);
    }
  } catch (error) {
    console.log(error);
    actions.payload.callback && actions.payload.callback(false);
  }
}

function* fetchPaymentProviders(actions: any) {
  try {
    const response: ResponseGenerator = yield api.request({
      method: `POST`,
      url: ENDPOINT.OUTLET_STATUS.getPaymentProviders,
      data: actions.payload.dataToPost,
    });
    if (!response.code) {
      actions.payload.callback && actions.payload.callback(true);
      yield put(savePaymentProviders({ response }));
    } else {
      actions.payload.callback && actions.payload.callback(false);
    }
  } catch (error) {
    console.log(error);
    actions.payload.callback && actions.payload.callback(false);
  }
}

function* fetchCurrentSyncStatus(actions: any) {
  try {
    const response: ResponseGenerator = yield api.request({
      method: `POST`,
      url: ENDPOINT.OUTLET_STATUS.getCurrentSyncStatus,
      data: actions.payload.dataToPost,
    });
    if (!response.code) {
      actions.payload.callback && actions.payload.callback(response);
    } else {
      actions.payload.callback && actions.payload.callback(response);
    }
  } catch (error) {
    console.log(error);
    actions.payload.callback && actions.payload.callback(error);
  }
}

function* fetchCurrentActivityUrl(actions: any) {
  try {
    const response: ResponseGenerator = yield api.request({
      method: 'GET',
      url: ENDPOINT.OUTLET_STATUS.activityDownload(actions.payload.mid),
    });
    if (!response.code) {
      actions.payload.callback && actions.payload.callback(response);
    } else {
      actions.payload.callback && actions.payload.callback(response);
    }
  } catch (error) {
    console.log(error);
    actions.payload.callback && actions.payload.callback(error);
  }
}


export default function* root() {
  yield takeEvery(getAllUsers.type, fetchAllUsers);
  yield takeEvery(getOutletsData.type, fetchOutletsData);
  yield takeEvery(getOutletOnlineStatus.type, fetchOutletOnlineStatus);
  yield takeEvery(getActivityLogs.type, fetchActivityLogs);
  yield takeEvery(getActivityErrorDetails.type, fetchActivityErrorDetails);
  yield takeEvery(toggleOutletStatus.type, outletStatusToggle);
  yield takeEvery(syncSingleOutletMenu.type, singleOutletMenuSync);
  yield takeEvery(outletStatusBulkUpdate.type, bulkOutletStatusUpdate);
  yield takeEvery(outletBulkMenuSync.type, bulkOutletMenuSync);
  yield takeEvery(getHelpCentreLink.type, fetchHelpCentreLink);
  yield takeEvery(getOutletLogisticStatus.type, fetchOutletLogisticStatus);
  yield takeEvery(updateOutletLogisticStatus.type, outletLogisticStatusUpdate);
  yield takeEvery(getSelfDeliveryChargeData.type, fetchSelfDeliveryChargeData);
  yield takeEvery(updateSelfDeliveryCharge.type, selfDeliveryChargeUpdate);
  yield takeEvery(getDeliveryTimings.type, fetchDeliveryTimings);
  yield takeEvery(updateDeliveryTimings.type, deliveryTimingsUpdate);
  yield takeEvery(getSelfDeliveryTimings.type, fetchSelfDeliveryTimings);
  yield takeEvery(updateSelfDeliveryTimings.type, selfDeliveryTimingsUpdate);
  yield takeEvery(getOutletConfiguration.type, fetchOutletConfiguration);
  yield takeEvery(saveOutletConfiguration.type, outletConfigurationSave);
  yield takeEvery(getAllWebhookEntities.type, fetchAllWebhookEntities);
  yield takeEvery(getPaymentProviders.type, fetchPaymentProviders);
  yield takeEvery(getCurrentSyncStatus.type, fetchCurrentSyncStatus);
  yield takeEvery(getActivityDownload.type, fetchCurrentActivityUrl)
}
